import React, { useEffect, useState, useRef, useContext } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Box, Flex, Grid, Link } from "theme-ui";
import { useNavigate } from "react-router-dom";

import { createClient, getClients } from "api/Clients";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";

import { CountryStates } from "helpers/CountryStates";
import { filterByKeys } from "helpers/default";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Tab, Tabs } from "components/ui/Tabs";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { BackArrowIcon, CheckMarkIcon } from "components/svg";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import { themevals } from "theme/themevals";
import Separator from "components/ui/Separator";
import Checkbox from "components/forms/Checkbox";
import SelectSearch from "components/forms/SelectSearch";

export default function ClientCreate() {
  const countryStates: any[] = Object.entries(CountryStates["US"]?.states || {}).map(([key, value]) => ({ value: key, label: value.name }));

  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);

  const submitCreate = async (values: any) => {
    const keys = [
      "type",
      "category",
      "name",
      "firstName",
      "lastName",
      "website",
      "phone",
      "companyAdmin",
      "address.line1",
      "address.line2",
      "address.city",
      "address.state",
      "address.postalCode",
      "contactEmail",
      "billingEmail",
      "parentId",
      "admin.firstName",
      "admin.lastName",
      "admin.email",
      "admin.clientId",
    ];
    const create = filterByKeys(keys, values);

    const res = await createClient({ create });
    if (res?.id) {
      toast.add("Client created.", "var(--theme-ui-colors-success)");
      
      // Track client creation
      void trackActivity({
        module: "clients",
        action: "create_client",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "client",
        entityId: res.id,
        metadata: {
          clientType: values.type === 1 ? "individual" : "company",
          clientName: values.type === 1 ? `${values.firstName} ${values.lastName}` : values.name,
          category: values.category,
          hasParent: !!values.parentId,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      
      navigate(`/clients/${res.id}`);
    } else {
      toast.add(res?.message ? res.message : "Error creating client", "var(--theme-ui-colors-danger)");
    }
  };

  const _validate = Yup.object().shape({
    type: Yup.number().required("Type is required."),
    name: Yup.string().when("type", {
      is: (v: number) => v === 2,
      then: (schema) => schema.required("This field is required."),
      otherwise: (schema) => schema.nullable(),
    }),
    firstName: Yup.string().when("type", {
      is: (v: number) => v === 1,
      then: (schema) => schema.required("This field is required."),
      otherwise: (schema) => schema.nullable(),
    }),
    lastName: Yup.string().when("type", {
      is: (v: number) => v === 1,
      then: (schema) => schema.required("This field is required."),
      otherwise: (schema) => schema.nullable(),
    }),
    contactEmail: Yup.string().required("This field is required."),
    address: Yup.object().shape({
      line1: Yup.string().when("$type", {
        is: (v: number) => v === 2,
        then: (schema) => schema.required("Address Line 1 is required."),
        otherwise: (schema) => schema.nullable(),
      }),
      city: Yup.string().when("$type", {
        is: (v: number) => v === 2,
        then: (schema) => schema.required("City is required."),
        otherwise: (schema) => schema.nullable(),
      }),
      state: Yup.string().when("$type", {
        is: (v: number) => v === 2,
        then: (schema) => schema.required("State is required."),
        otherwise: (schema) => schema.nullable(),
      }),
      postalCode: Yup.string().when("$type", {
        is: (v: number) => v === 2,
        then: (schema) => schema.required("Zip is required."),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
    admin: Yup.object()
      .shape({
        clientId: Yup.string().when("primaryClient", {
          is: (v: boolean) => v === false,
          then: (schema) => schema.required("This field is required."),
        }),
        firstName: Yup.string().when("primaryClient", {
          is: (v: boolean) => v === true,
          then: (schema) => schema.required("This field is required."),
        }),
        lastName: Yup.string().when("primaryClient", {
          is: (v: boolean) => v === true,
          then: (schema) => schema.required("This field is required."),
        }),
        email: Yup.string()
          .email("Invalid email format")
          .transform(function (value) {
            return value?.trim();
          })
          .nullable(), // removed the "email-not-same" test
      })
      .when("type", {
        is: (v: number) => v === 2,
        then: (schema) => schema.required("This field is required."),
        otherwise: (schema) => schema.nullable(),
      }),
  });

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Client";

    if (hasTrackedView.current) return;
    hasTrackedView.current = true;

    void trackActivity({
      module: "clients",
      action: "view_client_create",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: null,
      entityId: null,
      metadata: {},
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [user]);

  // Validation context state for Yup
  const [validationContext, setValidationContext] = useState({
    type: 1,
    contactEmail: "",
    billingEmail: "",
  });

  return (
    <>
      <Header></Header>
      <Page
        title="Add client"
        subtitle="Add a client and their details."
        backAction={
          <Link variant="back" href="/clients">
            <BackArrowIcon />
            Back to Clients
          </Link>
        }
      >
        <Formik initialValues={{ type: 1, primaryClient: true }} onSubmit={submitCreate} validationSchema={_validate} validationContext={validationContext}>
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => {
            // Sync validation context with Formik values
            useEffect(() => {
              setValidationContext({
                type: values.type,
                contactEmail: values.contactEmail,
                billingEmail: values.billingEmail,
              });
            }, [values.type, values.contactEmail, values.billingEmail]);

            return (
              <Form noValidate autoComplete="off">
                <Tabs
                  vertical
                  stickyFooter
                  footer={
                    <Flex sx={{ gap: "10px" }}>
                      <Link href="/clients" sx={{ ...themevals.buttons.secondaryOutline, textDecoration: "none", margin: ".25rem 0 .25rem 0" }}>
                        Cancel
                      </Link>
                      <BasicButton styles={{ margin: ".25rem 10px .25rem 10px" }} type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save Client
                      </BasicButton>
                    </Flex>
                  }
                >
                  <Tab itemKey="general">
                    <Grid gap={2} columns={[1, "1fr 2fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Basic Info
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Add Client Name and Phone.
                        </Subtext>
                      </Box>
                      <Box>
                        <Grid gap={3} columns={[1, 3]}>
                          <SelectInput
                            name="type"
                            label="Client Type"
                            value={values.type}
                            options={[
                              { value: 1, label: "Individual" },
                              { value: 2, label: "Company" },
                            ]}
                            onChange={(val: any) => {
                              if (val === 1) {
                                setFieldValue("name", "");
                                setFieldValue("parentId", undefined);
                              } else {
                                setFieldValue("parentId", undefined);
                              }
                              setFieldValue("type", val);
                            }}
                            $errors={errors.type && submitCount > 0 ? errors.type : null}
                          />
                          <SelectInput
                            name="category"
                            label="Client Category"
                            value={values.category}
                            options={[
                              { value: "builder", label: "Builder" },
                              { value: "owner", label: "Owner" },
                              { value: "manager", label: "Manager" },
                            ]}
                            onChange={(val: any) => setFieldValue("category", val)}
                            $errors={errors.category && submitCount > 0 ? errors.category : null}
                            readonly={values?.parentId ? true : false}
                          />
                          {values.type === 1 ? (
                            <SelectSearch
                              api={getClients}
                              params={{
                                filters: { type: 2 },
                              }}
                              multiple={false}
                              placeholder="None"
                              name="parentId"
                              label="Company Name"
                              value={values?.parentId}
                              keyLabel={["name"]}
                              keyValue="id"
                              preload
                              onChange={(val: any) => {
                                setFieldValue("parentId", val?.id);
                                console.log(val);
                                setFieldValue("category", val?.category);
                              }}
                              $errors={errors?.["parentId"] && submitCount > 0 ? errors?.["parentId"] : null}
                            />
                          ) : (
                            <TextInput
                              name="name"
                              label="Company Name"
                              value={values.name}
                              onChange={(val: any) => setFieldValue("name", val)}
                              $errors={errors.name && submitCount > 0 ? errors.name : null}
                            />
                          )}
                        </Grid>
                        <Grid gap={3} columns={[1, 3]}>
                          {values.type === 1 ? (
                            <>
                              <TextInput
                                name="firstName"
                                label="First Name"
                                value={values?.firstName}
                                onChange={(val: any) => setFieldValue("firstName", val)}
                                $errors={errors?.firstName && submitCount > 0 ? errors?.firstName : null}
                              />
                              <TextInput
                                name="lastName"
                                label="Last Name"
                                value={values?.lastName}
                                onChange={(val: any) => setFieldValue("lastName", val)}
                                $errors={errors?.lastName && submitCount > 0 ? errors?.lastName : null}
                              />
                            </>
                          ) : (
                            <>
                              <TextInput
                                name="website"
                                label="Website"
                                value={values?.website}
                                onChange={(val: any) => setFieldValue("website", val)}
                                $errors={errors?.website && submitCount > 0 ? errors?.website : null}
                                $customStyles={{ gridColumn: [1, "span 2"] }}
                              />
                            </>
                          )}
                          <TextInput
                            name="phone"
                            label="Phone"
                            value={values.phone}
                            onChange={(val: any) => setFieldValue("phone", val)}
                            $errors={errors?.phone && submitCount > 0 ? errors?.phone : null}
                          />
                        </Grid>
                        {values.parentId !== undefined && (
                          <Grid gap={3} columns={[1, 1]}>
                            <Checkbox
                              label="Grant Admin access"
                              checked={values.companyAdmin}
                              sublabel="Admins have full access to manage users and settings."
                              labelSize="14px"
                              onChange={(val) => {
                                setFieldValue("companyAdmin", val);
                              }}
                            />
                          </Grid>
                        )}
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={2} columns={[1, "1fr 2fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Address
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Add Client Address.
                        </Subtext>
                      </Box>
                      <Box>
                        <Flex sx={{ gap: "1rem" }}>
                          <TextInput
                            name="address[line1]"
                            label="Address 1"
                            value={values?.address?.line1}
                            onChange={(val: any) => setFieldValue("address.line1", val)}
                            $errors={
                              typeof errors?.address === "object" && errors?.address !== null && "line1" in errors.address && submitCount > 0 ? (errors.address as Record<string, string>).line1 : null
                            }
                            $customStyles={{ flexGrow: 2 }}
                          />
                          <TextInput
                            name="address[line2]"
                            label="Address 2"
                            value={values?.address?.line2}
                            onChange={(val: any) => setFieldValue("address.line2", val)}
                            $customStyles={{ flexGrow: 1 }}
                          />
                        </Flex>
                        <Flex sx={{ gap: "1rem" }}>
                          <TextInput
                            name="address[city]"
                            label="City"
                            value={values?.address?.city}
                            onChange={(val: any) => setFieldValue("address.city", val)}
                            $errors={
                              typeof errors?.address === "object" && errors?.address !== null && "city" in errors.address && submitCount > 0 ? (errors.address as Record<string, string>).city : null
                            }
                            $customStyles={{ width: "33.3333%" }}
                          />
                          <SelectInput
                            name="address[state]"
                            label="State"
                            value={values?.address?.state}
                            options={countryStates}
                            onChange={(val: any) => setFieldValue("address.state", val)}
                            clearable
                            $errors={
                              typeof errors?.address === "object" && errors?.address !== null && "state" in errors.address && submitCount > 0 ? (errors.address as Record<string, string>).state : null
                            }
                            $customStyles={{ width: "33.3333%" }}
                          />
                          <TextInput
                            name="address[postalCode]"
                            label="Zip"
                            value={values?.address?.postalCode}
                            onChange={(val: any) => setFieldValue("address.postalCode", val)}
                            $errors={
                              typeof errors?.address === "object" && errors?.address !== null && "postalCode" in errors.address && submitCount > 0
                                ? (errors.address as Record<string, string>).postalCode
                                : null
                            }
                            $customStyles={{ width: "33.3333%" }}
                          />
                        </Flex>
                      </Box>
                    </Grid>
                    <Separator />
                    <Grid gap={2} columns={[1, "1fr 2fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Email
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Add preferred emails for the client.
                        </Subtext>
                      </Box>
                      <Box>
                        <Grid gap={3} columns={[1, 2]}>
                          <TextInput
                            type="email"
                            name="contactEmail"
                            label="Contact Email"
                            value={values?.contactEmail}
                            onChange={(val: any) => setFieldValue("contactEmail", val)}
                            $errors={errors?.contactEmail && submitCount > 0 ? errors?.contactEmail : null}
                          />
                          <TextInput
                            type="email"
                            name="billingEmail"
                            label="Billing Email"
                            value={values?.billingEmail}
                            onChange={(val: any) => setFieldValue("billingEmail", val)}
                            $errors={errors?.billingEmail && submitCount > 0 ? errors?.billingEmail : null}
                          />
                        </Grid>
                      </Box>
                    </Grid>
                    {values.type !== 1 && (
                      <>
                        <Separator />
                        <Grid gap={2} columns={[1, "1fr 2fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                          <Box>
                            <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                              Primary Client
                            </H5>
                            <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                              Add primary individual client to who will have admin access to add other clients to this company.
                            </Subtext>
                          </Box>
                          <Box>
                            <Flex sx={{ gap: "1rem 2.5rem" }}>
                              <Checkbox
                                label="New individual client"
                                checked={values.primaryClient}
                                labelSize="14px"
                                onChange={(val) => {
                                  setFieldValue("primaryClient", val);
                                  setFieldValue("admin.clientId", val ? "" : values?.admin?.clientId);
                                }}
                              />
                              <Checkbox label="Existing individual client" checked={!values.primaryClient} onChange={(val) => setFieldValue("primaryClient", !val)} labelSize="14px" />
                            </Flex>
                            {values.primaryClient ? (
                              <Grid gap={3} columns={[1, 3]} sx={{ marginTop: "20px" }}>
                                <TextInput
                                  name="admin.firstName"
                                  label="First Name"
                                  value={values?.admin?.firstName}
                                  onChange={(val: any) => setFieldValue("admin.firstName", val)}
                                  $errors={errors?.["admin.firstName"] && submitCount > 0 ? errors?.["admin.firstName"] : null}
                                />
                                <TextInput
                                  name="admin.lastName"
                                  label="Last Name"
                                  value={values?.admin?.lastName}
                                  onChange={(val: any) => setFieldValue("admin.lastName", val)}
                                  $errors={errors?.["admin.lastName"] && submitCount > 0 ? errors?.["admin.lastName"] : null}
                                />
                                <TextInput
                                  type="email"
                                  name="admin.email"
                                  label="Email"
                                  value={values?.admin?.email}
                                  onChange={(val: any) => setFieldValue("admin.email", val)}
                                  $errors={
                                    typeof errors?.admin === "object" && errors?.admin !== null && "email" in errors.admin && submitCount > 0 ? (errors.admin as Record<string, string>).email : null
                                  }
                                />
                              </Grid>
                            ) : (
                              <SelectSearch
                                api={getClients}
                                params={{
                                  filters: {
                                    type: 1,
                                    "parent.id": ":null",
                                    contactEmail: `@${values.contactEmail?.split("@")?.[1]?.trim()}:contains`,
                                  },
                                }}
                                name="admin.clientId"
                                label="Existing Individual Client"
                                value={values?.admin?.clientId}
                                keyLabel={["name", "contactEmail"]}
                                keyValue="id"
                                labelDivider=" - "
                                preload
                                onChange={(val: any) => setFieldValue("admin.clientId", val?.id)}
                                $errors={errors?.["admin.clientId"] && submitCount > 0 ? errors?.["admin.clientId"] : null}
                              />
                            )}
                          </Box>
                        </Grid>
                      </>
                    )}
                  </Tab>
                </Tabs>
              </Form>
            );
          }}
        </Formik>
      </Page>
    </>
  );
}
